/**
 * ACTIVITY-CORE (L3) — card for one row of the activity feed.
 */
import { Show } from "solid-js";
import { Laptop, Loader2, Smartphone, Undo2, WifiOff } from "@fleet/icons";

export interface ActivityItemProps {
  sentence: string;
  timeLabel: string;
  native: boolean;
  offline: boolean;
  offlineLabel: string;
  revertible: boolean;
  reverting: boolean;
  undoLabel: string;
  onRevert: () => void;
}

export function ActivityItem(props: ActivityItemProps) {
  return (
    <article
      class="flex items-start gap-3 rounded-2xl border border-border/50 bg-card/70 p-3 shadow-sm backdrop-blur-sm"
      data-testid="activity-item"
    >
      <span class="mt-0.5 text-muted-foreground/70">
        <Show when={props.native} fallback={<Laptop class="h-4 w-4" />}>
          <Smartphone class="h-4 w-4" />
        </Show>
      </span>
      <div class="flex flex-1 flex-col gap-0.5">
        <p class="text-sm text-foreground">{props.sentence}</p>
        <div class="flex items-center gap-2 text-[11px] text-muted-foreground/70">
          <span>{props.timeLabel}</span>
          <Show when={props.offline}>
            <span
              class="inline-flex items-center gap-1 text-muted-foreground/60"
              data-testid="activity-offline"
            >
              <WifiOff class="h-3 w-3" />
              {props.offlineLabel}
            </span>
          </Show>
        </div>
      </div>
      <Show when={props.revertible}>
        <button
          type="button"
          data-testid="activity-undo"
          class="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          disabled={props.reverting}
          aria-label={props.undoLabel}
          onClick={() => {
            props.onRevert();
          }}
        >
          <Show when={props.reverting} fallback={<Undo2 class="h-3.5 w-3.5" aria-hidden="true" />}>
            <Loader2
              class="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          </Show>
          <span>{props.undoLabel}</span>
        </button>
      </Show>
    </article>
  );
}
